/**
 * UsEntryProfile — top-of-Section-2 callout for US (Scorecard-sourced)
 * bachelor programmes. Surfaces the two admissions signals the US
 * publishes at the institution level:
 *
 *   1. SAT / ACT median + 25th-75th percentile ranges
 *   2. Test-requirement status (test-required / -optional / -blind).
 *
 * Data comes from the university doc (`scorecard_admissions`) so every
 * programme at the same uni shows the same signals — this IS how the
 * US publishes admissions data (per-institution, not per-programme).
 *
 * Renders nothing when the uni lacks any admissions signals.
 */
import { Card } from "@/components/ui/card";
import { GraduationCap } from "lucide-react";

const TEST_REQ_LABEL = {
  1: "Required",
  2: "Recommended",
  3: "Neither required nor recommended",
  4: "Considered but not required",
  5: "Test-optional",
};

export function UsEntryProfile({ uni }) {
  const sa = uni?.scorecard_admissions;
  if (!sa) return null;
  const hasSAT = sa.sat_25th && sa.sat_75th;
  const hasACT = sa.act_25th && sa.act_75th;
  if (!hasSAT && !hasACT && !sa.acceptance_rate) return null;

  return (
    <Card
      className="p-4 mb-5 border-indigo-300 bg-indigo-50/40"
      data-testid="us-entry-profile"
    >
      <div className="flex items-center gap-2 mb-3">
        <GraduationCap className="h-4 w-4 text-indigo-800" />
        <div className="font-underground-sc text-[10px] tracking-wider text-indigo-900">
          Admissions profile · {uni.name}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-stone-800">
        {sa.acceptance_rate != null && (
          <div>
            <b>Institutional acceptance rate:</b>{" "}
            {(sa.acceptance_rate * 100).toFixed(1)}%
          </div>
        )}
        {hasSAT && (
          <div>
            <b>SAT range (25th-75th):</b> {sa.sat_25th}-{sa.sat_75th}
            {sa.sat_average ? ` · median ${sa.sat_average}` : ""}
          </div>
        )}
        {hasACT && (
          <div>
            <b>ACT range (25th-75th):</b> {sa.act_25th}-{sa.act_75th}
            {sa.act_median ? ` · median ${sa.act_median}` : ""}
          </div>
        )}
        {sa.test_requirements != null && (
          <div>
            <b>Standardised test:</b>{" "}
            {TEST_REQ_LABEL[sa.test_requirements] || "Varies"}
          </div>
        )}
      </div>

      <div className="mt-3 text-xs text-stone-500 italic">
        US universities publish admissions data at the institution level,
        not per programme — these numbers apply to every bachelor's applicant
        at {uni.name}. Highly selective programmes within the institution
        (e.g. engineering, business) often have stricter cut-offs than the
        institutional median.
      </div>

      <div className="mt-2 text-[10px] font-mono text-stone-500">
        Source: US Dept of Education — College Scorecard · verified{" "}
        {sa.fetched_at || ""}
      </div>
    </Card>
  );
}

export default UsEntryProfile;
